import { Brightness1 } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Api } from "../../api/Api";
import { thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import { IStore } from "../../store/IStore";
import { CustomerIdentificationDto } from "../../store/models/identification/CustomerIdentificationDto";
import { IdentificationStatus } from "../../store/models/identification/IdentificationStatus";
import { CardDesignDto } from "../../store/models/carddesign/CardDesignDto";
import { PassRequestDto } from "../../store/models/pass/PassRequestDto";
import { DateTag } from "../atoms/DateTag";
import { FormSelect } from "../atoms/FormSelect";
import ImsEmbeddedTable, { ImsHeadCell, ImsTableRow } from "../atoms/ImsEmbeddedTable";
import { COLOR_GRAY_4, KNISTR_GREEN, KNISTR_RED } from "../atoms/ImsMaterialTheme";
import ImsPaperHead from "../atoms/ImsPaperHead";

const Identifications = (props: ThunkProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { cardDesigns, customer } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCardDesign, setSelectedCardDesign] = useState<string>("");
  const [passNumber, setPassNumber] = useState<string>("");
  const [identifications, setIdentifications] = useState<CustomerIdentificationDto[]>([]);

  useEffect(() => {
    const loadIdentifications = async () => {
      if (customer?.customerNumber) {
        try {
          const identifications = await Api.getCustomerIdentifications(customer.customerNumber);
          setIdentifications(identifications);
        } catch (error) {
          thunkCreateErrorNotification("Error", "Error loading identifications!");
        }
      }
    };
    loadIdentifications();
  }, [customer?.customerNumber]);

  const resetDialogState = () => {
    setSelectedCardDesign("");
    setPassNumber("");
  };

  const handleCreatePass = async () => {
    if (!selectedCardDesign) {
      thunkCreateErrorNotification("Error", t("identifications.error.selectCardDesign"));
      return;
    }

    if (isPassNumberRequired(selectedCardDesign) && (!passNumber || passNumber.trim() === "")) {
      thunkCreateErrorNotification("Error", "Pass number cannot be empty or contain only spaces.");
      return;
    }

    try {
      const selectedType = getSelectedCardDesign(selectedCardDesign);
      const passRequest = createPassRequest(selectedType);
      await Api.createPass(passRequest);
      await reloadIdentifications();
      resetDialogState();
      setDialogOpen(false);
    } catch (error) {
      thunkCreateErrorNotification("Error", t("identifications.error.unexpected"));
    }
  };

  const getSelectedCardDesign = (cardDesignNumber: string) => {
    return cardDesigns.find((it) => it.cardDesignNumber === cardDesignNumber);
  };

  const createPassRequest = (selectedType: CardDesignDto | undefined): PassRequestDto => {
    if (!selectedType || !selectedType.externalCardDesignNumber) {
      thunkCreateErrorNotification("Error", t("identifications.error.invalidCardDesign"));
      throw new Error(t("identifications.error.invalidCardDesign"));
    }

    if (!customer || !customer.externalCustomerId) {
      thunkCreateErrorNotification("Error", t("identifications.error.invalidCustomer"));
      throw new Error(t("identifications.error.invalidCustomer"));
    }

    const baseRequest = {
      externalPassTypeNumber: selectedType.externalCardDesignNumber,
      status: "ACTIVE",
      validFrom: new Date().toISOString().split("T")[0],
      connections: [
        {
          type: "CUSTOMER",
          externalConnectionId: customer.externalCustomerId,
        },
      ],
    };

    if (!selectedType.templateName) {
      // When no template exists, the pass number has to be set manually
      if (!passNumber) {
        throw new Error(t("identifications.error.cardNumberRequired"));
      }
      return {
        passId: passNumber,
        ...baseRequest,
      };
    }

    return baseRequest;
  };

  const reloadIdentifications = async () => {
    if (customer?.customerNumber) {
      const identifications = await Api.getCustomerIdentifications(customer.customerNumber);
      setIdentifications(identifications);
    }
  };

  const isPassNumberRequired = (cardDesignNumber: string): boolean => {
    const selectedType = cardDesigns.find((it) => it.cardDesignNumber === cardDesignNumber);
    return !selectedType?.templateName;
  };

  const handleOpenDialog = () => {
    resetDialogState();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    resetDialogState();
    setDialogOpen(false);
  };

  const handleDropdownChange = (value: string) => {
    setSelectedCardDesign(value);
    isPassNumberRequired(value);
  };

  const getCardDesignName = (externalCardDesignNumber: string): string | undefined =>
    cardDesigns?.find((cardDesign) => cardDesign.externalCardDesignNumber === externalCardDesignNumber)?.name;

  const headCells: ImsHeadCell[] = [
    {
      title: t("customerCareDetailsPage.cardHeadingStatus"),
      align: "center",
    },
    {
      title: t("customerCareDetailsPage.cardHeadingCardNumber"),
      align: "center",
    },
    {
      title: t("customerCareDetailsPage.cardHeadingType"),
      align: "center",
    },
    {
      title: t("customerCareDetailsPage.cardHeadingValidFrom"),
      align: "center",
    },
  ];

  identifications.sort((a, b) => a.externalCardDesignNumber.localeCompare(b.externalCardDesignNumber));
  const rows: ImsTableRow[] = identifications.map((identification, index) => ({
    key: index,
    value: identification,
    nodes: [
      <Brightness1
        style={{
          fill: getIdentificationStatusColor(identification.status),
          marginRight: theme.spacing(1),
          paddingBottom: 2,
        }}
      />,
      identification.cardNumber,
      getCardDesignName(identification.externalCardDesignNumber),
      <DateTag date={identification.validFrom} />,
    ],
  }));

  return (
    <Grid item md={12}>
      <Paper
        style={{
          padding: theme.spacing(3, 0, 3, 0),
          margin: theme.spacing(6, 0, 0, 0),
        }}
      >
        <Grid container justifyContent="space-between" alignItems="center" style={{ padding: theme.spacing(0, 3) }}>
          <ImsPaperHead text={t("customerCareDetailsPage.cardHeadline")} />
          <Button data-testid="create-pass-button" variant="contained" color="primary" onClick={handleOpenDialog}>
            {t("customerCareDetailsPage.createCardButton")}
          </Button>
        </Grid>
        <Grid
          item
          md={12}
          style={{
            padding: theme.spacing(0),
          }}
        >
          <ImsEmbeddedTable headCells={headCells} rows={rows} />
        </Grid>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("customerCareDetailsPage.createCardDialogTitle")}</DialogTitle>
        <DialogContent style={{ minHeight: "200px", paddingTop: theme.spacing(2) }}>
          <FormSelect
            testId="cardDesignDropdown"
            name="cardDesignNumber"
            label={t("setup.selectCardDesign")}
            options={[{ label: t("setup.placeholder"), value: "" }].concat(
              cardDesigns.map((cardDesign) => ({
                label: cardDesign.name,
                value: cardDesign.cardDesignNumber as string,
              }))
            )}
            fullWidth={true}
            initialValues={[]}
            onChange={(value) => handleDropdownChange(value)}
          />
          {selectedCardDesign && isPassNumberRequired(selectedCardDesign) && (
            <TextField
              label={t("setup.cardNumber")}
              value={passNumber}
              onChange={(e) => setPassNumber(e.target.value.trimStart())}
              fullWidth
              margin="normal"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            variant="contained"
            style={{ margin: theme.spacing(0, 0, 2, 2) }}
          >
            {t("setup.buttonCancel")}
          </Button>
          <Button
            onClick={handleCreatePass}
            color="primary"
            variant="contained"
            style={{ margin: theme.spacing(0, 0, 2, 2) }}
            disabled={!selectedCardDesign || (isPassNumberRequired(selectedCardDesign) && !passNumber)}
          >
            {t("setup.buttonSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export function getIdentificationStatusColor(status: IdentificationStatus): string {
  switch (status) {
    case IdentificationStatus.ACTIVE:
      return KNISTR_GREEN;
    case IdentificationStatus.INACTIVE:
      return KNISTR_RED;
    default:
      return COLOR_GRAY_4;
  }
}

const mapStateToProps = (state: IStore) => ({
  identifications: state.identifications?.identifications,
  cardDesigns: state.identifications?.cardDesigns,
  customer: state.customerCare.customer,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({}, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(Identifications);
